/* =============================================================
   ExchangeTermsSection.jsx — Manage exchange terms
   -------------------------------------------------------------
   Source of truth: semesters table (not derived from students).

   Features:
     - Create new terms (free-text name)
     - Rename terms (double-click)
     - Show student count per term (by semester_id)
     - Close (archive) a term → sets archived=true on all students
       with that semester_id
   ============================================================= */

import { useState, useEffect, useCallback } from 'react';
import { supabase }        from '../lib/supabase';
import { useAuth }         from '../context/AuthContext';
import { useSemesters }    from '../hooks/useSemesters';

const SEMESTERS_EVENT = 'unixplore:semesters-updated';


export default function ExchangeTermsSection() {
  const { user }   = useAuth();
  const { semesters, loading, saving, error, createSemester, updateSemester, deleteSemester } = useSemesters();

  const [studentCounts, setStudentCounts] = useState({}); // { semesterId: { incoming, outgoing } }
  const [countsLoading, setCountsLoading] = useState(false);
  const [archiving,     setArchiving]     = useState(null); // semesterId being archived
  const [confirm,       setConfirm]       = useState(null); // semesterId awaiting confirmation
  const [archiveError,  setArchiveError]  = useState(null);

  // Create form
  const [creating, setCreating] = useState(false);
  const [newName,  setNewName]  = useState('');


  /* ── Load student counts per semester ────────────────────── */
  const loadCounts = useCallback(async () => {
    if (!semesters.length) return;
    setCountsLoading(true);
    const ids = semesters.map(s => s.id);
    const { data } = await supabase
      .from('students')
      .select('semester_id, type')
      .in('semester_id', ids)
      .eq('archived', false);

    const counts = {};
    for (const row of data ?? []) {
      if (!counts[row.semester_id]) counts[row.semester_id] = { incoming: 0, outgoing: 0 };
      if (row.type === 'incoming') counts[row.semester_id].incoming++;
      if (row.type === 'outgoing') counts[row.semester_id].outgoing++;
    }
    setStudentCounts(counts);
    setCountsLoading(false);
  }, [semesters]);

  useEffect(() => { loadCounts(); }, [loadCounts]);


  /* ── Archive a term ──────────────────────────────────────── */
  const closeTerm = async (semesterId) => {
    setArchiving(semesterId);
    setArchiveError(null);

    // 1. Archive all active students in this term
    const { error: studentsErr } = await supabase
      .from('students')
      .update({ archived: true })
      .eq('semester_id', semesterId)
      .eq('archived', false);

    if (studentsErr) { setArchiveError(studentsErr.message); setArchiving(null); return; }

    // 2. Mark semester as closed → disappears from all active lists
    //    (record stays in DB so students keep their semester_id for restore)
    const { error: semErr } = await supabase
      .from('semesters')
      .update({ is_closed: true })
      .eq('id', semesterId);

    if (semErr) { setArchiveError(semErr.message); setArchiving(null); return; }

    setConfirm(null);
    window.dispatchEvent(new CustomEvent(SEMESTERS_EVENT));
    setArchiving(null);
  };


  /* ── Create ──────────────────────────────────────────────── */
  const handleCreate = async () => {
    if (!newName.trim()) return;
    await createSemester(newName.trim());
    setNewName('');
    setCreating(false);
  };


  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">

      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
        <span className="material-symbols-outlined text-[18px] text-indigo-600">event_repeat</span>
        <h2 className="font-display text-base font-semibold text-[#0b1c30]">Exchange Terms</h2>
        <span className="text-xs text-slate-400 font-normal">— open and close your exchange terms</span>
      </div>

      <div className="p-4 space-y-3">

        {/* Errors */}
        {(error || archiveError) && (
          <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
            <span className="material-symbols-outlined text-[16px]">error</span>
            {error || archiveError}
          </div>
        )}

        {loading ? (
          <div className="space-y-2 animate-pulse">
            {[1, 2].map(i => <div key={i} className="h-14 bg-slate-100 rounded-xl" />)}
          </div>
        ) : semesters.length === 0 && !creating ? (
          <div className="text-center py-6">
            <span className="material-symbols-outlined text-slate-300 text-[32px]">event_available</span>
            <p className="text-sm font-medium text-slate-500 mt-2">No terms yet</p>
            <p className="text-xs text-slate-400 mt-1">Create your first term to get started.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {semesters.map(sem => {
              const counts     = studentCounts[sem.id] ?? { incoming: 0, outgoing: 0 };
              const total      = counts.incoming + counts.outgoing;
              const isArchiving = archiving === sem.id;
              const isConfirm  = confirm   === sem.id;

              return (
                <TermCard
                  key={sem.id}
                  semester={sem}
                  total={total}
                  incoming={counts.incoming}
                  outgoing={counts.outgoing}
                  isConfirm={isConfirm}
                  isArchiving={isArchiving}
                  onRename={updateSemester}
                  onDelete={deleteSemester}
                  onConfirm={() => setConfirm(sem.id)}
                  onCancelConfirm={() => setConfirm(null)}
                  onClose={() => closeTerm(sem.id)}
                  saving={saving}
                />
              );
            })}
          </div>
        )}

        {/* Create new term */}
        {creating ? (
          <div className="flex flex-wrap items-center gap-2">
            <input
              autoFocus
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleCreate();
                if (e.key === 'Escape') { setCreating(false); setNewName(''); }
              }}
              placeholder='e.g. "Fall 2025" or "Anno Acc. 25/26"'
              className="flex-1 min-w-[160px] px-3 py-2 text-sm border border-indigo-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] placeholder-slate-400"
            />
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleCreate}
                disabled={!newName.trim() || saving}
                className="px-4 py-2 text-sm font-medium bg-[#4343d5] text-white rounded-xl hover:bg-indigo-700 disabled:opacity-40 transition-colors"
              >
                {saving ? 'Creating…' : 'Create'}
              </button>
              <button
                onClick={() => { setCreating(false); setNewName(''); }}
                className="px-3 py-2 text-sm border border-slate-200 text-slate-500 rounded-xl hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setCreating(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-dashed border-indigo-200 text-indigo-500 rounded-xl hover:bg-indigo-50 hover:border-indigo-300 transition-colors w-full justify-center"
          >
            <span className="material-symbols-outlined text-[16px]">add</span>
            New term
          </button>
        )}

        <p className="text-[11px] text-slate-400 pt-1">
          When a semester ends, close its term to archive all its students. Archived students are removed from the active lists but remain visible in Analytics.
        </p>
      </div>
    </div>
  );
}


/* ─── TERM CARD ──────────────────────────────────────────────── */
function TermCard({ semester, total, incoming, outgoing, isConfirm, isArchiving,
                    onRename, onDelete, onConfirm, onCancelConfirm, onClose, saving }) {
  const [editingName, setEditingName] = useState(false);
  const [nameVal,     setNameVal]     = useState(semester.name);
  const [confirmDel,  setConfirmDel]  = useState(false);

  const handleNameBlur = () => {
    setEditingName(false);
    const trimmed = nameVal.trim();
    if (trimmed && trimmed !== semester.name) onRename(semester.id, trimmed);
    else setNameVal(semester.name);
  };

  return (
    <div className={`px-4 py-3 rounded-xl border transition-colors ${
      isConfirm ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-100'
    }`}>
      <div className="flex items-center justify-between gap-2">

        {/* Icon */}
        <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
          <span className="material-symbols-outlined text-[16px] text-indigo-600">calendar_month</span>
        </div>

        {/* Name */}
        <div className="flex-1 min-w-0">
          {editingName ? (
            <input
              autoFocus
              value={nameVal}
              onChange={e => setNameVal(e.target.value)}
              onBlur={handleNameBlur}
              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
              className="w-full px-2 py-0.5 text-sm font-semibold text-[#0b1c30] border border-indigo-300 rounded-lg focus:outline-none"
            />
          ) : (
            <p
              className="text-sm font-semibold text-[#0b1c30] cursor-text hover:text-indigo-600 transition-colors"
              onDoubleClick={() => setEditingName(true)}
              title="Double-click to rename"
            >
              {semester.name}
            </p>
          )}
          <p className="text-xs text-slate-400 mt-0.5">
            {total} student{total !== 1 ? 's' : ''}
            {total > 0 && (
              <>
                <span className="mx-1.5 text-slate-300">·</span>
                {incoming} in
                <span className="mx-1.5 text-slate-300">·</span>
                {outgoing} out
              </>
            )}
          </p>
        </div>

        {/* Actions */}
        {!isConfirm && !confirmDel && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={onConfirm}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-200 text-slate-500 rounded-lg hover:border-red-200 hover:text-red-600 transition-colors"
            >
              <span className="material-symbols-outlined text-[13px]">archive</span>
              Close Term
            </button>
            <button
              onClick={() => setConfirmDel(true)}
              className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-slate-100 transition-colors"
              title="Delete term"
            >
              <span className="material-symbols-outlined text-[15px]">delete</span>
            </button>
          </div>
        )}

        {/* Delete confirmation */}
        {confirmDel && !isConfirm && (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-xs text-red-500 font-medium">Delete?</span>
            <button onClick={() => setConfirmDel(false)} className="px-2 py-1 text-xs border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
            <button onClick={() => onDelete(semester.id)} className="px-2 py-1 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
          </div>
        )}
      </div>

      {/* Close term confirmation */}
      {isConfirm && (
        <div className="mt-3 pt-3 border-t border-red-200">
          <p className="text-xs text-red-700 font-medium mb-2">
            Archive all {total} student{total !== 1 ? 's' : ''} in "{semester.name}"?
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onCancelConfirm}
              className="flex-1 px-3 py-1.5 text-xs font-medium border border-slate-200 text-slate-600 rounded-lg hover:bg-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onClose}
              disabled={isArchiving}
              className="flex-1 px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-1"
            >
              {isArchiving
                ? <span className="material-symbols-outlined text-[13px] animate-spin">progress_activity</span>
                : <span className="material-symbols-outlined text-[13px]">archive</span>
              }
              {isArchiving ? 'Archiving…' : 'Confirm'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
